import moment from 'moment';

export const EXPORT_ROW_WARNING_THRESHOLD = 100000;

export type DatasetDimension = 'tenant' | 'company' | 'bu' | 'date';

// Filter key used by the API and the table column keys that represent each dimension.
const DIMENSIONS: Array<{ dimension: DatasetDimension; filterKey: string; columnKeys: string[] }> = [
  { dimension: 'tenant', filterKey: 'tenant_id', columnKeys: ['tenant_id', 'tenant_name'] },
  { dimension: 'company', filterKey: 'company_id', columnKeys: ['company_id', 'company_name'] },
  { dimension: 'bu', filterKey: 'bu_id', columnKeys: ['bu_id', 'bu_name'] },
  { dimension: 'date', filterKey: 'date_added', columnKeys: ['date_added'] },
];

const isEmpty = (value: any) =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

const isDateLike = (v: any) => moment.isMoment(v) || v instanceof Date;

// Number of distinct values a filter selects (0 = unfiltered, Infinity = date range spanning days).
const distinctCount = (dimension: DatasetDimension, value: any): number => {
  if (isEmpty(value)) return 0;
  const values: any[] = Array.isArray(value) ? value : [value];
  if (dimension === 'date' && values.length === 2 && isDateLike(values[0]) && isDateLike(values[1])) {
    // RangePicker [start, end]: single only when both fall on the same day
    return moment(values[0]).isSame(moment(values[1]), 'day') ? 1 : Infinity;
  }
  return new Set(values.map((v) => (isDateLike(v) ? moment(v).format('YYYY-MM-DD') : String(v)))).size;
};

/**
 * Returns the dimensions (among those present on the table) that the filter does NOT pin to
 * exactly one value. An empty result means the filter selects a single dataset (or the table
 * has none of the dataset columns, so the check does not apply).
 */
export const getUnpinnedDimensions = (
  filterKeys: { [key: string]: any } | undefined,
  columnKeys: string[]
): Array<{ dimension: DatasetDimension; count: number }> => {
  const present = new Set(columnKeys.filter(Boolean));
  const result: Array<{ dimension: DatasetDimension; count: number }> = [];
  DIMENSIONS.forEach(({ dimension, filterKey, columnKeys: keys }) => {
    if (!keys.some((k) => present.has(k))) return;
    const count = distinctCount(dimension, filterKeys?.[filterKey]);
    if (count !== 1) result.push({ dimension, count });
  });
  return result;
};

const LABELS: { [key in DatasetDimension]: string } = {
  tenant: 'tenants',
  company: 'companies',
  bu: 'BUs',
  date: 'dates',
};

/** Builds the reasons to warn before export; empty array means export immediately. */
export const getExportWarnings = (
  rowCount: number,
  filterKeys: { [key: string]: any } | undefined,
  columnKeys: string[]
): string[] => {
  const warnings: string[] = [];
  const rows = Number(rowCount) || 0;
  if (rows > EXPORT_ROW_WARNING_THRESHOLD) {
    warnings.push(
      `More than ${EXPORT_ROW_WARNING_THRESHOLD.toLocaleString('en-US')} rows — the export will be split into multiple CSV files in a zip.`
    );
  }
  const unpinned = getUnpinnedDimensions(filterKeys, columnKeys);
  if (unpinned.length > 0) {
    const parts = unpinned.map(({ dimension, count }) =>
      count === 0 ? `all ${LABELS[dimension]}` : `several ${LABELS[dimension]}`
    );
    warnings.push(
      `Not limited to a single dataset: covers ${parts.join(', ')}. Apply filters to narrow it.`
    );
  }
  return warnings;
};
